import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { BridgeApiService } from '../services/bridge-api.service';
import { CHAIN_MAP, getNetworkName, getNetworkIcon } from '../utils/chains';

@Component({
  selector: 'app-quote-preview-v3',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule],
  templateUrl: './quote-preview-v3.component.html',
  styleUrls: ['./quote-preview-v3.component.scss'] // Ajout d'un SCSS dédié pour un design pro, inspiré Uniswap
})
export class QuotePreviewComponentV3 implements OnChanges, OnDestroy {
  @Input() quote: any;
  @Input() signature: string = '';
  @Input() fiatCode = 'USD';
  @Input() fiatRates: Record<string, number> = { USD: 1 };
  @Input() swapStatus: 'idle' | 'approve' | 'pending' | 'success' | 'error' | 'cancelled' = 'idle';
  @Input() swapHash: string | null = null;
  @Input() swapError: string | null = null;
  @Input() validateAlternative?: (route: any) => { disabled: boolean; reasons: string[] };
  @Output() executeRequest = new EventEmitter<void>();
  @Output() selectAlternative = new EventEmitter<any>();
  @Output() copyToClipboard = new EventEmitter<string>();
  @Input() swapDisabled = false;
  @Input() nativeTokenUsd = 0;
  @Input() gasPriceWei: bigint = 0n;
  @Input() fullGasWei: bigint = 0n;
  @Output() close = new EventEmitter<void>();

  // États UI pour V2 : inspiré Uniswap (dépliable, focus sur received amount + provider)
  showDetails = false; // Toggle pour détails avancés
  showAlternatives = false; // Toggle pour alternatives (dépliable comme Uniswap)
  private autoHideTimeout?: any;
  private pendingTimeout?: any;
  private tooltipTimeout?: any;
  showTooltip = false;

  // Fallbacks pour gas (compatibilité)
  private _fallbackFullGasWei: bigint = 0n;
  private selectedAltKey: string | null = null;
  private currentQuoteKey: string | null = null;
  // Nouveau pour V3 : gestion des détails expandés par alternative
  expandedAlts: Set<string> = new Set<string>();
  readonly CHAIN_MAP = CHAIN_MAP;
  // Ajoute ceci dans la classe
  keyOf = (_: number, r: any) =>
    (this.getProviderKey?.(r)) ||
    r?.id ||
    `${r?.providerName ?? r?.toolKey ?? 'route'}-${_}`;

  constructor(private translate: TranslateService, private bridgeApi: BridgeApiService) { }

  // Méthodes utilitaires (réutilisées et étendues pour V2)
  private norm(s: any): string {
    return String(s ?? '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim();
  }

  private getProviderKey(x: any): string {
    return this.norm(
      x?.providerKey ?? x?.ProviderKey ?? x?.toolKey ?? x?.ToolKey ?? x?.tool ?? x?.Tool ?? x?.source ?? x?.Source ?? x?.executorKey ?? x?.ExecutorKey ?? x?.providerName ?? x?.ProviderName ?? x?.name
    );
  }

  private toNum(v: any): number {
    return typeof v === 'bigint' ? Number(v) : Number(v ?? 0);
  }

  private canonicalNativeSymbol(chainId?: number, raw?: string): string {
    const map: Record<number, string> = { 1: 'ETH', 10: 'ETH', 56: 'BNB', 137: 'MATIC', 42161: 'ETH', 8453: 'ETH' };
    if (chainId && map[chainId]) return map[chainId];
    const up = (raw || '').toUpperCase();
    if (up === 'POL' || up === 'MATIC') return map[137];
    return up || 'ETH';
  }

  get networkIconUrl(): string | null {
    const id = this.quote?.fromChainId ?? this.quote?.chainId ?? this.quote?.action?.fromChainId ?? this.quote?.estimate?.fromChainId ?? null;
    return getNetworkIcon(id);
  }

  get networkName(): string {
    const id = this.quote?.fromChainId ?? this.quote?.chainId ?? this.quote?.action?.fromChainId ?? this.quote?.estimate?.fromChainId ?? null;
    return getNetworkName(id);
  }

  get sellTokenSymbol(): string { return this.quote?.sellTokenSymbol ?? ''; }
  get buyTokenSymbol(): string { return this.quote?.buyTokenSymbol ?? ''; }

  // Nouveaux getters pour V2 : Focus sur received amount, provider, et rate (inspiré Uniswap)
  get providerName(): string {
    return this.quote?.providerName ?? this.quote?.ProviderName ?? this.getProviderKey(this.quote) ?? 'Uniswap API'; // Placeholder pro
  }

  get providerLogo(): string | null {
    return this.quote?.providerLogo ?? this.quote?.ToolLogoURI ?? this.quote?.toolLogoURI ?? this.quote?.logoURI ?? this.quote?.logo ?? this.quote?.Icon ?? this.quote?.icon ?? this.quote?.toolDetails?.logoURI ?? null;
  }

  get estimatedReceived(): string {
    const sym = this.getTokenLabel(this.buyTokenSymbol);
    const decimals =
      Number(this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);

    // 1) Si on a une valeur déjà formatée, on l’utilise telle quelle
    const formatted = this.quote?.formattedBuyAmount;
    let value: number;

    if (formatted !== undefined && formatted !== null && String(formatted) !== '') {
      value = Number(formatted);
    } else {
      // 2) Sinon on normalise le "raw" (wei-like)
      const raw = this.quote?.toAmount ?? this.quote?.ToAmount ?? this.quote?.toAmountMin ?? 0;
      const rawNum = typeof raw === 'bigint' ? Number(raw) : Number(raw || 0);
      value = rawNum / Math.pow(10, decimals);
    }

    if (!isFinite(value)) value = 0;
    return `${value.toFixed(6)} ${sym}`;
  }

  private formatFiat(usd: number): string {
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const fiat = usd * rate;
    const locale = this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US';
    if (fiat > 0 && fiat < 0.01) {
      return '< ' + (0.01).toLocaleString(locale, { style: 'currency', currency: this.fiatCode });
    }
    const underOne = fiat > 0 && fiat < 1;
    return fiat.toLocaleString(locale, {
      style: 'currency',
      currency: this.fiatCode,
      minimumFractionDigits: underOne ? 4 : 2,
      maximumFractionDigits: underOne ? 6 : 2,
    });
  }
  isAltExpanded(r: any): boolean {
    return this.expandedAlts.has(this.getProviderKey(r));
  }
  get mainFee() {
    const sym = this.canonicalNativeSymbol(Number(this.quote?.fromChainId));

    // 1) Préférence: champs LiFi déjà normalisés (V2)
    const lifiNative = Number((this.quote as any)?.lifiNetworkFeeNative ?? 0);
    const lifiUsd = Number((this.quote as any)?.lifiNetworkFeeUsd ?? 0);
    if (lifiNative > 0) {
      return {
        textEth: `${lifiNative.toFixed(6)} ${sym}`,
        textFiat: lifiUsd > 0 ? `≈ ${this.formatFiat(lifiUsd)}` : undefined,
      };
    }

    // 2) Fallback: totalWei (fullGasWei || _fallbackFullGasWei || 0)
    const totalWei =
      (this.fullGasWei && this.fullGasWei > 0n) ? this.fullGasWei :
        (this._fallbackFullGasWei && this._fallbackFullGasWei > 0n) ? this._fallbackFullGasWei : 0n;

    const native = Number(totalWei) / 1e18;
    const textEth = native ? `${native.toFixed(6)} ${sym}` : '';

    const usdPerNative = Number(
      this.quote?.gasTokenPriceUsd ??
      this.quote?.GasTokenPriceUsd ??
      this.nativeTokenUsd ??
      0
    );

    const usd = (usdPerNative && native) ? native * usdPerNative : 0;

    return {
      textEth,
      textFiat: usd > 0 ? `≈ ${this.formatFiat(usd)}` : undefined,
    };
  }

  // Remplace/ajoute ce getter
  get estimatedReceivedFiat(): string {
    // 1) Prix USD du token reçu
    const priceUsd = Number(this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0);

    // 2) Montant reçu (déjà formaté → sinon toAmount + décimales)
    let amount = Number(this.quote?.formattedBuyAmount ?? 0);
    if (!(amount > 0)) {
      const dec = Number(
        this.quote?.buyTokenDecimals ??
        this.quote?.BuyTokenDecimals ??
        this.quote?.toToken?.decimals ?? 18
      );

      const raw = this.quote?.toAmount ?? this.quote?.ToAmount ?? 0;
      let rawNum = 0;
      if (typeof raw === 'bigint') rawNum = Number(raw);
      else if (typeof raw === 'string' && raw.startsWith('0x')) {
        try { rawNum = Number(BigInt(raw)); } catch { rawNum = 0; }
      } else {
        rawNum = Number(raw || 0);
      }
      amount = rawNum / Math.pow(10, dec);
    }

    // 3) Valeur en USD → format fiat à 2 décimales fixes
    const usd = priceUsd * amount;
    if (!(usd > 0)) return '';

    const code = this.fiatCode ?? 'USD';
    const rate = this.fiatRates?.[code] ?? 1;
    const locale = (this as any).getLocaleForCurrency
      ? (this as any).getLocaleForCurrency(code)
      : (code === 'EUR' ? 'fr-FR' : 'en-US');

    const fiat = usd * rate;
    return `≈ ${fiat.toLocaleString(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }


  getRouteEstimatedBuyFiat(r: any): string {
    // 1) USD direct si présent (plusieurs variantes de casse)
    let usd =
      Number(r?.toAmountUsd ?? 0) ||
      Number(r?.ToAmountUsd ?? 0) ||
      Number((r as any)?.toAmountUSD ?? 0);

    // 2) Sinon on calcule: montant * prix USD
    if (!(usd > 0)) {
      // montant reçu (préférer le formaté)
      let amt = Number(r?.formattedBuyAmount ?? 0);
      if (!(amt > 0)) {
        const dec = Number(
          r?.toTokenDecimals ?? r?.buyTokenDecimals ??
          r?.toToken?.decimals ?? this.quote?.buyTokenDecimals ??
          this.quote?.BuyTokenDecimals ?? 18
        );

        const raw = r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? r?.ToAmount ?? 0;
        let rawNum = 0;
        if (typeof raw === 'bigint') rawNum = Number(raw);
        else if (typeof raw === 'string' && raw.startsWith('0x')) {
          try { rawNum = Number(BigInt(raw)); } catch { rawNum = 0; }
        } else { rawNum = Number(raw || 0); }
        amt = rawNum / Math.pow(10, dec);
      }

      const priceUsd = Number(
        r?.buyTokenUsdPrice ?? r?.BuyTokenUsdPrice ??
        this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0
      );

      if (amt > 0 && priceUsd > 0) usd = amt * priceUsd;
    }

    if (!(usd > 0)) return '';

    // 3) Format FIAT à **2 décimales fixes**
    const code = this.fiatCode ?? 'USD';
    const rate = this.fiatRates?.[code] ?? 1;
    const locale = (this as any).getLocaleForCurrency
      ? (this as any).getLocaleForCurrency(code)
      : (code === 'EUR' ? 'fr-FR' : 'en-US');

    const fiat = usd * rate;

    return `≈ ${fiat.toLocaleString(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }



  get exchangeRate(): string {
    const sellSym = this.getTokenLabel(this.sellTokenSymbol);
    const buySym = this.getTokenLabel(this.buyTokenSymbol);

    const sellDecimals = Number(this.quote?.sellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18);
    const buyDecimals = Number(this.quote?.buyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);

    // Utilitaires
    const toNum = (v: any): number => {
      if (v == null || v === '') return NaN;
      if (typeof v === 'bigint') return Number(v);
      const s = String(v);
      if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
      const n = Number(s);
      return isFinite(n) ? n : NaN;
    };

    // 1) On privilégie les valeurs déjà formatées (unités humaines)
    let sell = toNum(this.quote?.formattedSellAmount);
    let buy = toNum(this.quote?.formattedBuyAmount);

    // 2) Sinon, fallback sur les montants "bruts" + normalisation par decimals
    if (!isFinite(sell)) {
      const rawSell = toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount);
      if (isFinite(rawSell)) sell = rawSell / Math.pow(10, sellDecimals);
    }
    if (!isFinite(buy)) {
      const rawBuy = toNum(this.quote?.toAmount ?? this.quote?.buyAmount ?? this.quote?.ToAmount);
      if (isFinite(rawBuy)) buy = rawBuy / Math.pow(10, buyDecimals);
    }

    if (!isFinite(sell) || !isFinite(buy) || sell <= 0 || buy <= 0) return '—';

    // 3) 1 SELL = (buy / sell) BUY
    const rate = buy / sell;
    const rateStr = rate >= 1000 ? rate.toFixed(0) : rate.toFixed(6);

    return `1 ${sellSym} = ${rateStr} ${buySym}`;
  }


  // Détails dépliables (comme Uniswap : fees, network cost, slippage)
  get routeFee(): string {
    // Frais de route (e.g., 0.25% Uniswap)
    return this.quote?.routeFeePct ? `${this.quote.routeFeePct}%` : '0.3%'; // Placeholder
  }

  // Option 1: si tu as déjà une méthode formatFiat(usd:number): string (cf. patch précédent),
  // alors le getter peut être ultra simple :
  get networkCost(): string {
    const gasFeeUsd = Number(this.quote?.gasFeeUsd ?? this.quote?.GasFeeUsd ?? 0);
    if (!(gasFeeUsd > 0)) return '—';
    // formatFiat prend un montant en USD et applique le taux + locale + décimales adaptatives
    return this.formatFiat(gasFeeUsd); // ex: "€0.0018" ou "< €0.01"
  }


  get priceImpact(): string {
    // décimales
    const sd = Number(this.quote?.sellTokenDecimals ?? this.quote?.SellTokenDecimals ?? this.quote?.fromToken?.decimals ?? 18);
    const bd = Number(this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ?? this.quote?.toToken?.decimals ?? 18);

    // montants bruts → humains
    const toNum = (v: any) => {
      if (v == null || v === '') return NaN;
      if (typeof v === 'bigint') return Number(v);
      const s = String(v); if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
      const n = Number(s); return isFinite(n) ? n : NaN;
    };

    let sell = toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.SellAmount);
    let buy = toNum(this.quote?.toAmount ?? this.quote?.buyAmount ?? this.quote?.ToAmount);

    if (!isFinite(sell) || !isFinite(buy)) return '—';
    sell = sell / Math.pow(10, sd);
    buy = buy / Math.pow(10, bd);

    // prix USD
    const sellUsd = Number(this.quote?.sellTokenUsdPrice ?? this.quote?.SellTokenUsdPrice ?? 0);
    const buyUsd = Number(this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0);

    // fallback si LiFi a directement donné from/toAmountUSD
    const fromUsd = Number((this.quote as any)?.fromAmountUsd ?? 0);
    const toUsd = Number((this.quote as any)?.toAmountUsd ?? 0);
    const buyFair = (sellUsd > 0 && buyUsd > 0)
      ? (sell * sellUsd) / buyUsd
      : (fromUsd > 0 && buy > 0) ? (fromUsd / buyUsd) : NaN;

    if (!isFinite(buyFair) || buyFair <= 0) return '—';

    let impact = 1 - (buy / buyFair);

    // optionnel: retirer l’aggregator fee si dispo (isoler l’impact "pur")
    const integFeeUsd = Number((this.quote as any)?.integratorFeeUsd ?? 0);
    if (integFeeUsd > 0 && sellUsd > 0) {
      const buyFairAdj = ((sell * sellUsd) - integFeeUsd) / buyUsd;
      if (buyFairAdj > 0) impact = 1 - (buy / buyFairAdj);
    }

    const pct = Math.max(0, impact) * 100;
    return `${pct.toFixed(pct >= 1 ? 2 : 3)}%`;
  }


  get maxSlippage(): string {
    // Effet de glissement max (comme image : 1.04%)
    return this.quote?.maxSlippagePct ? `${this.quote.maxSlippagePct}%` : '0.5%'; // Placeholder
  }

  // Gas fee (réutilisé)
  get displayedGasFee(): string {
    const nat = this.canonicalNativeSymbol(Number(this.quote?.fromChainId));
    const totalWei = this.fullGasWei > 0n ? this.fullGasWei : this._fallbackFullGasWei > 0n ? this._fallbackFullGasWei : 0n;
    const eth = Number(totalWei) / 1e18;
    return `${eth.toFixed(6)} ${nat}`;
  }

  getGasFeeFiat(): string | null {
    if (!this.nativeTokenUsd) return null;
    const totalWei = this.fullGasWei > 0n ? this.fullGasWei : this._fallbackFullGasWei > 0n ? this._fallbackFullGasWei : 0n;
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const eth = Number(totalWei) / 1e18;
    const usd = eth * this.nativeTokenUsd;
    const fiat = usd * rate;
    return (Math.round(fiat * 100) / 100).toLocaleString(this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US', { style: 'currency', currency: this.fiatCode });
  }

  getTokenLabel(symbol: string): string {
    const key = 'TOKENS.' + symbol.toUpperCase();
    const translated = this.translate.instant(key);
    return translated !== key ? translated : symbol;
  }

  // Alternatives (compact : received + provider, détails on click)
  get otherRoutes(): any[] {
    const all = Array.isArray(this.quote?.singleRoutes) ? this.quote.singleRoutes : [];
    const current = this.quote || {};
    return all.filter((r: any) => !this.isSameProvider(r, current));
  }

  private getRouteKey(r: any): string {
    const raw = r?.providerKey ?? r?.ProviderKey ?? r?.toolKey ?? r?.ToolKey ?? r?.tool ?? r?.Tool ?? r?.source ?? r?.Source ?? r?.providerName ?? r?.ProviderName ?? r?.name ?? '';
    return String(raw || '').toLowerCase().trim();
  }

  isAltSelected(r: any): boolean {
    const k = this.getProviderKey(r);
    return !!k && (k === this.selectedAltKey || k === this.currentQuoteKey);
  }

  private isSameProvider(a: any, b: any): boolean {
    return this.getProviderKey(a) === this.getProviderKey(b);
  }

  getRouteProvider(r: any): string {
    return (r?.providerName ?? r?.ProviderName) || this.getProviderKey(r);
  }

  getRouteLogo(r: any): string | null {
    return r?.providerLogo ?? r?.ToolLogoURI ?? r?.toolLogoURI ?? r?.logoURI ?? r?.logo ?? r?.Icon ?? r?.icon ?? r?.toolDetails?.logoURI ?? null;
  }

  getRouteEstimatedBuy(r: any): string {
    const raw = Number(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? 0);
    const dec = Number(r?.toTokenDecimals ?? r?.toToken?.decimals ?? this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ?? 18);
    const val = raw / Math.pow(10, dec);
    return `${this.formatAmount(val, 6)} ${this.getRouteBuySymbol(r)}`;
  }

  private getRouteBuySymbol(r: any): string {
    return r?.buyTokenSymbol ?? r?.toTokenSymbol ?? r?.action?.toToken?.symbol ?? this.quote?.buyTokenSymbol ?? this.quote?.BuyTokenSymbol ?? '—';
  }

  formatAmount(value: number, decimals: number): string {
    return value.toFixed(decimals);
  }

  isAlternativeDisabled(r: any): boolean {
    if (this.swapDisabled) return true;
    const tx = r?.transactionRequest ?? r?.TransactionRequest ?? null;
    const hasTx = !!(tx?.to && tx?.data) || !!(r?.to && r?.data) || !!(r?.target && r?.data);
    const hasTyped = !!(r?.domain && r?.types && r?.value);
    return !(hasTx || hasTyped) || (this.validateAlternative?.(r)?.disabled ?? false);
  }

  onValidateAlternative(route: any) {
    const key = this.getProviderKey(route);
    this.selectedAltKey = key;
    this.selectAlternative.emit(route);
    // Optionnel : toggle détails pour cette alternative
    this.showDetails = true;
  }

  // UI Toggles (inspiré Uniswap : clean, minimal interactions)
  toggleDetails() {
    this.showDetails = !this.showDetails;
  }

  toggleAlternatives() {
    this.showAlternatives = !this.showAlternatives;
  }

  toggleTooltip() {
    this.showTooltip = !this.showTooltip;
    if (this.showTooltip) {
      this.tooltipTimeout = setTimeout(() => this.showTooltip = false, 3000);
    }
  }




  /** ───────────── Helpers pour alternatives ───────────── */


  private _toNum(v: any): number {
    if (v == null || v === '') return NaN;
    if (typeof v === 'bigint') return Number(v);
    const s = String(v);
    if (s.startsWith('0x')) { try { return Number(BigInt(s)); } catch { return NaN; } }
    const n = Number(s);
    return isFinite(n) ? n : NaN;
  }
  private _toWei(v: any): bigint {
    try {
      if (typeof v === 'bigint') return v;
      if (typeof v === 'number') return BigInt(Math.trunc(v));
      if (typeof v === 'string') return v ? BigInt(v) : 0n;
    } catch { }
    return 0n;
  }

  /** ───────────── ROUTE_FEE ─────────────
   *  Essayez d'avoir r.routeFeePct côté back; sinon fallback raisonnable.
   */
  getRouteFee(r: any): string {
    const pct = r?.routeFeePct ?? this.quote?.routeFeePct;
    return (pct != null) ? `${pct}%` : '0.3%';
  }

  /** ───────────── NETWORK_COST (fiat) ───────────── */
  getRouteNetworkCost(r: any): string {
    const usd = Number(r?.gasFeeUsd ?? r?.GasFeeUsd ?? 0);
    if (usd > 0) return this.formatFiat(usd);

    // Fallback: (wei -> natif) * priceUsd
    const wei = this._toWei(r?.gasAmountWei ?? r?.GasAmountWei ?? r?.totalNetworkFee ?? r?.TotalNetworkFee ?? 0);
    const native = Number(wei) / 1e18;
    const usdPerNative = Number(
      r?.gasTokenPriceUsd ?? r?.GasTokenPriceUsd ??
      this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd ??
      this.nativeTokenUsd ?? 0
    );
    const estUsd = native && usdPerNative ? native * usdPerNative : 0;
    return estUsd > 0 ? this.formatFiat(estUsd) : '—';
  }

  /** ───────────── NETWORK_FEE (natif + ≈ fiat) ───────────── */
  getRouteDisplayedGasFee(r: any): { textEth: string; textFiat?: string } {
    const chainId = Number(r?.fromChainId ?? this.quote?.fromChainId);
    const symbol = this.canonicalNativeSymbol(chainId, r?.GasTokenSymbol ?? r?.gasTokenSymbol);
    const decimals = Number(r?.GasTokenDecimals ?? r?.gasTokenDecimals ?? 18) || 18;
    const pow10 = Math.pow(10, decimals);

    // 1) montant en "wei"
    let feeWei =
      this._toWei(r?.lifiGasAmountWei ?? r?.LifiGasAmountWei) ||
      (() => {
        const p = this._toNum(r?.lifiGasPriceWei ?? r?.LifiGasPriceWei);
        const g = this._toNum(r?.lifiEstimatedGas ?? r?.LifiEstimatedGas);
        return (p && g) ? BigInt(Math.round(p * g)) : 0n;
      })() ||
      this._toWei(r?.gasAmountWei ?? r?.GasAmountWei ?? r?.totalNetworkFee ?? r?.TotalNetworkFee);

    const native = Number(feeWei) / pow10;

    // 2) affichage natif (avec seuil)
    const NATIVE_MIN = 1e-8;
    let textEth = '';
    if (native > 0) {
      textEth = native >= NATIVE_MIN
        ? `${native.toFixed(8)} ${symbol}`
        : `<${NATIVE_MIN.toFixed(8)} ${symbol}`;
    }

    // 3) USD/Fiat
    let usd = this._toNum(r?.lifiGasFeeUsd ?? r?.LifiGasFeeUsd ?? r?.gasFeeUsd ?? r?.GasFeeUsd);
    if (!usd && native > 0) {
      const usdPerNative =
        this._toNum(r?.lifiGasTokenPriceUsd ?? r?.LifiGasTokenPriceUsd) ||
        this._toNum(r?.gasTokenPriceUsd ?? r?.GasTokenPriceUsd) ||
        (this.nativeTokenUsd || 0);
      if (usdPerNative) usd = native * usdPerNative;
    }

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const locale = this.fiatCode === 'EUR' ? 'fr-FR' : 'en-US';
    const FIAT_MIN = 0.01;
    let textFiat = '';
    const fiat = usd * rate;
    if (fiat > 0) {
      textFiat = fiat >= FIAT_MIN
        ? `≈ ${fiat.toLocaleString(locale, { style: 'currency', currency: this.fiatCode })}`
        : `≈ < ${FIAT_MIN.toLocaleString(locale, { style: 'currency', currency: this.fiatCode })}`;
    }

    return { textEth, textFiat };
  }


  /** ───────────── PRICE_IMPACT (%) ───────────── */
  getRoutePriceImpact(r: any): string {
    // décimales (sell sur la quote, buy sur la route)
    const sd = Number(
      r?.sellTokenDecimals ?? this.quote?.sellTokenDecimals ?? this.quote?.SellTokenDecimals ??
      r?.fromToken?.decimals ?? this.quote?.fromToken?.decimals ?? 18
    );
    const bd = Number(
      r?.toTokenDecimals ?? r?.buyTokenDecimals ?? this.quote?.buyTokenDecimals ?? this.quote?.BuyTokenDecimals ??
      r?.toToken?.decimals ?? this.quote?.toToken?.decimals ?? 18
    );

    // montants normalisés
    let sell = this._toNum(this.quote?.formattedSellAmount);
    if (!isFinite(sell)) {
      const rawSell = this._toNum(this.quote?.fromAmount ?? this.quote?.sellAmount ?? this.quote?.FromAmount);
      if (isFinite(rawSell)) sell = rawSell / Math.pow(10, sd);
    }
    let buy = this._toNum(r?.formattedBuyAmount);
    if (!isFinite(buy)) {
      const rawBuy = this._toNum(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount ?? r?.ToAmount);
      if (isFinite(rawBuy)) buy = rawBuy / Math.pow(10, bd);
    }
    if (!isFinite(sell) || !isFinite(buy) || sell <= 0 || buy <= 0) return '—';

    // prix USD (pivot)
    const sellUsd = Number(this.quote?.sellTokenUsdPrice ?? this.quote?.SellTokenUsdPrice ?? 0);
    const buyUsd = Number(this.quote?.buyTokenUsdPrice ?? this.quote?.BuyTokenUsdPrice ?? 0);
    const fromUsd = Number(this.quote?.fromAmountUsd ?? r?.fromAmountUsd ?? 0); // fallback éventuel

    let buyFair = (sellUsd > 0 && buyUsd > 0)
      ? (sell * sellUsd) / buyUsd
      : (fromUsd > 0 && buyUsd > 0) ? (fromUsd / buyUsd) : NaN;

    if (!isFinite(buyFair) || buyFair <= 0) return '—';

    // option : retirer l’intégrator fee USD si dispo (impact “pur”)
    const integFeeUsd = Number(r?.integratorFeeUsd ?? this.quote?.integratorFeeUsd ?? 0);
    if (integFeeUsd > 0 && sellUsd > 0) {
      const adj = ((sell * sellUsd) - integFeeUsd) / (buyUsd || 1);
      if (adj > 0 && isFinite(adj)) buyFair = adj;
    }

    const impact = Math.max(0, 1 - (buy / buyFair));
    const pct = impact * 100;
    return `${pct.toFixed(pct >= 1 ? 2 : 3)}%`;
  }

  /** ───────────── MAX_SLIPPAGE (%) ───────────── */
  getRouteMaxSlippage(r: any): string {
    const v = r?.maxSlippagePct ?? this.quote?.maxSlippagePct;
    return (v != null) ? `${v}%` : '0.5%';
  }











  // Compatibilité : applyGasFromQuote, etc.
  applyGasFromQuote(q: any) {
    const totalWei = BigInt(q?.totalNetworkFee ?? q?.TotalNetworkFee ?? 0);
    this._fallbackFullGasWei = totalWei;
    this.nativeTokenUsd = Number(q?.gasTokenPriceUsd ?? q?.GasTokenPriceUsd ?? 0);
    if ((!this.nativeTokenUsd || this.nativeTokenUsd === 0) && totalWei > 0n) {
      const feeUsd = Number(q?.gasFeeUsd ?? q?.GasFeeUsd ?? 0);
      if (feeUsd) {
        const feeEth = Number(totalWei) / 1e18;
        this.nativeTokenUsd = feeEth ? (feeUsd / feeEth) : 0;
      }
    }
  }

  onShareClick(): void {
    const baseUrl = window.location.origin + '/';
    const tokenIn = this.sellTokenSymbol;
    const tokenOut = this.buyTokenSymbol;
    const amountIn = this.quote?.formattedSellAmount;
    const shareUrl = `${baseUrl}?tokenIn=${tokenIn}&tokenOut=${tokenOut}&amountIn=${amountIn}`;
    if (navigator.share) {
      navigator.share({ title: 'SmartSwap', text: this.translate.instant('DEFI_OPPORTUNITY'), url: shareUrl });
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        // Toast feedback (pro, comme Uniswap)
        setTimeout(() => { }, 3000);
      });
    }
  }

  closePreview() { this.close.emit(); }

  getFormattedSwapError(err: any): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return JSON.stringify(err);
  }

  isUserCancelled(err: string | null): boolean {
    return err?.includes('cancelled') ?? false; // Placeholder
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['quote']) {
      const newKey = this.getProviderKey(this.quote) || null;
      this.currentQuoteKey = newKey;
      if (!this.selectedAltKey) this.selectedAltKey = newKey;
      this.applyGasFromQuote(this.quote); // Compat
    }
    // Timers pour status (réutilisé)
    clearTimeout(this.pendingTimeout);
    clearTimeout(this.autoHideTimeout);
    if (this.swapStatus === 'pending') {
      this.pendingTimeout = setTimeout(() => {
        if (this.swapStatus === 'pending') this.swapStatus = 'idle';
      }, 30000);
    } else if (this.swapStatus && this.swapStatus !== 'idle') {
      this.autoHideTimeout = setTimeout(() => {
        if (this.swapStatus && this.swapStatus !== 'pending') this.swapStatus = 'idle';
      }, 5000);
    }
  }
  toggleAltDetails(route: any) {
    const key = this.getProviderKey(route);
    if (this.expandedAlts.has(key)) {
      this.expandedAlts.delete(key);
    } else {
      this.expandedAlts.add(key);
    }
  }
  ngOnDestroy() {
    clearTimeout(this.pendingTimeout);
    clearTimeout(this.autoHideTimeout);
    clearTimeout(this.tooltipTimeout);
  }
}
